import { useEffect, useMemo, useRef } from 'react';

interface CandleStripProps {
  count?: number;
  seed?: number;
  className?: string;
  height?: number;
}

/**
 * A row of candles that grow from the baseline when scrolled into view, then
 * hold. Echoes the candlesticks in the logo.
 *
 * Structural only — the shape is a seeded walk and encodes nothing, which is
 * why it carries no axis and no labels. Same rule the product's own panels
 * follow: motion may suggest a market, it may not imply a reading.
 */
export function CandleStrip({ count = 22, seed = 3, className, height = 64 }: CandleStripProps) {
  const ref = useRef<HTMLDivElement>(null);

  const candles = useMemo(() => {
    let s = seed * 1013904223;
    const rand = () => {
      s |= 0;
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    // Amplitude matters: a timid walk collapses into a flat smear that reads as
    // a rule, not a chart. These figures give visible bodies and a range that
    // uses most of the strip's height.
    let level = 0.5;
    return Array.from({ length: count }, () => {
      const drift = (rand() - 0.48) * 0.34;
      const open = level;
      level = Math.min(0.86, Math.max(0.14, level + drift));
      const close = level;

      const bodyTop = Math.min(open, close);
      const bodyH = Math.max(0.07, Math.abs(close - open));
      // Wicks extend past the body on both sides.
      const over = 0.04 + rand() * 0.12;
      const wickH = Math.min(0.96, bodyH + over * 2);
      const wickTop = Math.max(0.02, bodyTop - over);

      return { up: close >= open, bodyTop, bodyH, wickTop, wickH };
    });
  }, [count, seed]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      el.setAttribute('data-candles', 'in');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-candles', 'in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`candles ${className ?? ''}`}
      style={{ height }}
      data-candles=""
      aria-hidden="true"
    >
      {candles.map((c, i) => (
        <span
          key={i}
          className={`candle ${c.up ? 'is-up' : 'is-down'}`}
          style={{ ['--i' as string]: i }}
        >
          <i
            className="candle__wick"
            style={{ top: `${c.wickTop * 100}%`, height: `${c.wickH * 100}%` }}
          />
          <i
            className="candle__body"
            style={{ top: `${c.bodyTop * 100}%`, height: `${c.bodyH * 100}%` }}
          />
        </span>
      ))}
    </div>
  );
}
